import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { Loader } from '../../components/ui';
import { supabase } from '../../lib/supabase'; // Using the regular client not service role

interface JobStatus {
  id: string;
  status: string;
  timestamp: string;
  processed: number;
  successful: number;
  regenerated?: boolean;
}

export function NoteSummaryBatchJob() {
  const [isLoading, setIsLoading] = useState(false);
  const [notesCount, setNotesCount] = useState<number | null>(null);
  const [recentJobs, setRecentJobs] = useState<JobStatus[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Count notes that have no summaries yet but have content
  const countNotesNeedingSummaries = async () => {
    try {
      // Get note count from the database
      const { count, error } = await supabase
        .from('notes')
        .select('id', { count: 'exact', head: true })
        .is('summary', null)
        .gt('content', ' '.repeat(100)); // Only count notes with sufficient content

      if (error) throw error;
      return count;
    } catch (err) {
      console.error('Error counting notes:', err);
      return null;
    }
  };

  // Fetch notes count
  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Count notes needing summaries
      const count = await countNotesNeedingSummaries();
      setNotesCount(count);

      // We're not using a table to track jobs now, so we start with empty array
      // Jobs will be tracked in memory during the session

      setError(null);
    } catch (err) {
      console.error('Error fetching note summary data:', err);
      setError('Failed to load note summary data');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new job to generate or regenerate summaries
  const startJob = async (regenerateExisting = false) => {
    try {
      setIsLoading(true);

      // Call the Netlify function to start processing
      const functionUrl = `/.netlify/functions/note-summary`; // Use the dedicated note summary function

      // Add a job to the recent jobs list with regenerate info
      const jobId = Date.now().toString();
      const newJob = {
        id: jobId,
        status: 'processing',
        timestamp: new Date().toISOString(),
        processed: 0,
        successful: 0,
        regenerated: regenerateExisting,
      };

      setRecentJobs(prev => [newJob, ...prev].slice(0, 5)); // Keep at most 5 jobs

      // Use the note summary endpoint
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batchSize: 25,
          regenerateExisting: regenerateExisting,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Function execution failed: ${errorText}`);
      }

      // Parse the response
      const result = await response.json();

      // Update job status with actual values from the function
      setRecentJobs(prev =>
        prev.map(job =>
          job.id === jobId
            ? {
                ...job,
                status: 'completed',
                processed: result.processed || 0,
                successful: result.successful || 0,
                regenerated: result.regenerated || false,
              }
            : job
        )
      );

      // Refresh to see updated status
      await fetchData();
    } catch (err) {
      console.error('Error starting note summary job:', err);
      setError('Failed to start summary generation job');

      // Update job status to failed
      setRecentJobs(prev =>
        prev.map(job => (job.status === 'processing' ? { ...job, status: 'failed' } : job))
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="bg-gray-800 rounded-lg shadow-2xl p-6 mb-8 border border-gray-700">
      <h2 className="text-xl font-semibold mb-4 text-white">Note Summary Generator</h2>

      {isLoading ? (
        <div className="flex items-center justify-center py-4 text-white">
          <Loader size="small" color="text-blue-400" className="mr-2" />
          <span>Loading...</span>
        </div>
      ) : error ? (
        <div className="bg-red-900/20 border border-red-500 rounded-md p-4 mb-4 text-red-400">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 bg-gray-900/50 border border-gray-600 rounded-md p-4">
            <h3 className="text-blue-300 font-medium text-sm mb-2">About AI Generated Summaries</h3>
            <p className="text-gray-300 text-sm mb-3">
              This feature uses AI to automatically generate concise summaries for notes. These
              summaries are displayed in note previews instead of showing the beginning of the
              content, making it easier to quickly find relevant notes.
            </p>
            <p className="text-gray-300 text-sm mb-3">
              Only notes with substantial content (over 100 characters) will receive summaries. The
              processing happens in small batches to minimize API costs and server load.
            </p>
            <p className="text-gray-300 text-sm">
              <strong>Current status:</strong>{' '}
              {notesCount !== null ? `${notesCount} notes` : 'Unknown number of notes'} waiting for
              AI summaries.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={() => startJob(false)}
              disabled={isLoading || (notesCount !== null && notesCount === 0)}
              className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Generate New Summaries
            </button>

            <button
              onClick={() => startJob(true)}
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Regenerate All Summaries
            </button>
          </div>

          {recentJobs.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 text-white">Recent Jobs</h3>
              <div className="border border-gray-700 rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Started
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {recentJobs.map(job => (
                      <tr key={job.id} className="hover:bg-gray-700 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-300">{job.id.slice(-6)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.status === 'completed'
                                ? 'bg-green-900/20 text-green-400'
                                : job.status === 'failed'
                                  ? 'bg-red-900/20 text-red-400'
                                  : job.status === 'processing'
                                    ? 'bg-blue-900/20 text-blue-400'
                                    : 'bg-yellow-900/20 text-yellow-400'
                            }`}
                          >
                            {job.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {job.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(job.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {job.processed > 0 ? (
                            <span>
                              {job.successful} / {job.processed} successful
                              {job.regenerated && (
                                <span className="ml-2 text-xs px-2 py-0.5 bg-purple-900/20 text-purple-400 rounded-full">
                                  Regenerated
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-500">Not started</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
